import React, {useCallback, useEffect, useState} from 'react';
import {ChevronLeft, ChevronRight, MessageSquare, X} from 'lucide-react';
import {PhoneHQAvatar} from 'ch3-ui-lib';
import {AuthenticatedImage} from './AuthenticatedImage.tsx';
import {AuthenticatedVideo} from './AuthenticatedVideo.tsx';
import type {RoomMediaItem} from '../../utils/roomMedia.ts';

interface MediaViewerProps {
    items: RoomMediaItem[];
    initialIndex: number;
    roomName: string;
    senderName: (userId: string) => string;
    onClose: () => void;
    /** Closes the viewer and scrolls the chat to the message. */
    onShowInChat: (eventId: string) => void;
}

/**
 * Viewer for the Multimedia tab (D-CHAT-05/S05): who shared it and when,
 * "Show in chat", previous / next with the arrow keys, and "N of M".
 */
export const MediaViewer: React.FC<MediaViewerProps> = ({items, initialIndex, roomName, senderName, onClose, onShowInChat}) => {
    const [index, setIndex] = useState(initialIndex);
    const item = items[index];
    const go = useCallback((delta: number) => setIndex(i => Math.min(items.length - 1, Math.max(0, i + delta))), [items.length]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') go(-1);
            if (e.key === 'ArrowRight') go(1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go, onClose]);

    if (!item) return null;
    const name = senderName(item.sender);
    const date = new Date(item.ts);
    const when = `${date.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'})} · ${date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} · ${roomName}`;
    const round = 'flex h-11 w-11 items-center justify-center bg-white/[0.12] text-white hover:bg-white/20 disabled:opacity-30';

    return (
        <div role="dialog" aria-modal="true" aria-label="Media viewer" data-testid="chat.media-viewer"
             className="phq-anim-pop fixed inset-0 z-[60] flex flex-col bg-zinc-900 text-white">
            <div className="flex h-[72px] shrink-0 items-center gap-3 px-4">
                <PhoneHQAvatar name={name} size="40px"/>
                <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-base font-semibold leading-[1.4]">{name}</span>
                    <span className="truncate text-[13px] leading-[1.35] text-zinc-300">{when}</span>
                </div>
                <button type="button" onClick={() => onShowInChat(item.eventId)}
                        className="flex h-11 items-center gap-2 rounded-[10px] bg-brand px-4 text-sm font-semibold hover:bg-brand-hover">
                    <MessageSquare size={16} aria-hidden="true" className="!p-0 !text-white"/>
                    Show in chat
                </button>
                <button type="button" aria-label="Close viewer" onClick={onClose} className={`${round} rounded-xl`}>
                    <X size={18} aria-hidden="true" className="!p-0 !text-white"/>
                </button>
            </div>

            <div className="flex min-h-0 flex-1 items-center justify-center gap-6 px-4">
                <button type="button" aria-label="Previous" onClick={() => go(-1)} disabled={index === 0} className={`${round} rounded-full`}>
                    <ChevronLeft size={20} aria-hidden="true" className="!p-0 !text-white"/>
                </button>
                <div className="flex h-full max-h-[calc(100vh-200px)] min-w-0 flex-1 items-center justify-center">
                    {item.kind === 'photo' ? (
                        <AuthenticatedImage key={item.eventId} src={item.url} alt={`Photo shared by ${name}`}
                                            encryptedFile={item.encryptedFile} mimeType={item.mimeType}
                                            className="max-h-full max-w-full rounded-[10px] object-contain"/>
                    ) : (
                        <AuthenticatedVideo key={item.eventId} src={item.url} encryptedFile={item.encryptedFile} mimeType={item.mimeType}
                                            controls className="max-h-full max-w-full rounded-[10px]"/>
                    )}
                </div>
                <button type="button" aria-label="Next" onClick={() => go(1)} disabled={index === items.length - 1} className={`${round} rounded-full`}>
                    <ChevronRight size={20} aria-hidden="true" className="!p-0 !text-white"/>
                </button>
            </div>

            <div className="flex h-14 shrink-0 items-center justify-center text-[13px] font-semibold text-zinc-300" aria-live="polite">
                {index + 1} of {items.length}
            </div>
        </div>
    );
};

export default MediaViewer;
